/**
 * `createApiEvidencePolicySource` — the `metadata-registry-api` *confidence
 * policy* port over the shared `ApiClient`.
 *
 * The confidence policy decides where each domain's floor sits and what happens
 * to a judgement below it (keep / flag / demote / withhold). It has always
 * resolved along `system → customer → org → tenant → team → user`; until this
 * port existed the only write path was an admin editing a whole AppConfig
 * document, so a tenant could not see their own floors, let alone change them.
 *
 * The read returns the whole LADDER — every rung with what it authored, the
 * merged effective section, and a per-knob origin — rather than only what it
 * resolves to. That is the difference between a number and an answer: "0.8" is
 * not actionable, "0.8, inherited from your organisation" is.
 *
 * Canonical `/metadata-evidence-policy` paths are rewritten to
 * `internal/metadata-registry-api` by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { METADATA_PATHS } from "@authe/fe-commons/metadata-analytics";
import type {
  DomainEnforcement,
  LadderTier,
  ResolvedLadder,
} from "@authe/fe-commons/evidence";

/** What the server serves alongside the ladder so an editor offers real choices. */
export interface PolicyVocabulary {
  /** Domain ids the platform actually resolves. */
  domains: string[];
  /** `keep` | `flag` | `demote` | `withhold`. */
  actions: string[];
  /** Each domain's behaviour before anyone configured it. */
  default_actions: Record<string, string>;
  /**
   * The actions each domain's gate can honour. Protocol detection, for one,
   * keeps or withholds — offering it "keep, marked" saved a choice the gate
   * quietly stepped down to "keep". Absent on an older deploy: offer all.
   */
  supported_actions?: Record<string, string[]>;
  /**
   * Each domain's built-in floor (`value: null` = the domain has no floor), so a
   * row shows the number in force instead of "inherit".
   */
  platform_floors?: Record<string, { value: number | null; detail?: string | null; config_key?: string | null }>;
  /** Scalar knobs (thresholds, corroboration) outside the per-domain channels. */
  knobs: string[];
  /** Platform high/medium badge boundaries. */
  tier_cutoffs?: Record<string, number>;
  /**
   * Platform weight tables per domain — what each named input is worth.
   *
   * Only the domains the server can enumerate (today: `third_party`, whose keys
   * are the provider registry in `commons`). A domain absent here gets no
   * editor rather than a blank box.
   */
  base_tables?: Record<string, Record<string, number>>;
  /**
   * Whether anything evaluates each domain (`commons.evidence.enforcement`):
   * `enforced` | `conditional` | `not_enforced`, with a reason. The save refuses
   * a value for a `not_enforced` domain or an `inert_keys` weight, so the editor
   * shows those read-only. Absent on an older deploy: offer everything.
   */
  domain_enforcement?: Record<string, DomainEnforcement>;
}

export interface PolicyLadderResponse extends ResolvedLadder {
  vocabulary: PolicyVocabulary;
  /**
   * What the policy actually did, per domain, over `activity_window_days`.
   *
   * Served on the policy read rather than from its own route because it answers
   * the same screen's other question: a floor whose effect an operator cannot
   * see is a setting they have no way to tune.
   */
  activity?: Record<string, Record<string, number>>;
  activity_window_days?: number;
}

/** What `POST …/preview` answers: the ladder as it WOULD resolve. */
export type PolicyPreview = Pick<ResolvedLadder, "effective" | "origins" | "problems"> & {
  /**
   * What the save would refuse — this rung's section only, where `problems`
   * covers the merged ladder (a coarser rung's inert setting is not a reason
   * this save fails). Absent on an older deploy.
   */
  save_problems?: string[];
};

export interface EvidencePolicySource {
  /** The whole ladder for the caller's tenancy + the editing vocabulary. */
  getLadder(): Promise<PolicyLadderResponse>;
  /**
   * What the ladder WOULD resolve to with `section` authored at `tier`.
   *
   * Its own route, never a flag on the save: an operator should be able to see
   * which knobs move before anything is written, and a client bug must not be
   * able to turn one into the other.
   */
  preview(tier: LadderTier, section: Record<string, unknown>): Promise<PolicyPreview>;
  /** Author this rung's SPARSE slice — only the knobs it sets. */
  save(
    tier: LadderTier,
    section: Record<string, unknown>,
    note?: string,
  ): Promise<void>;
  /** Drop this rung's override; it goes back to inheriting. */
  clear(tier: LadderTier): Promise<void>;
}

export function createApiEvidencePolicySource(
  client: ApiClient,
): EvidencePolicySource {
  const path = METADATA_PATHS.evidencePolicy;
  return {
    async getLadder() {
      return unwrapData(await client.get(path)) as PolicyLadderResponse;
    },
    async preview(tier, section) {
      return unwrapData(
        await client.post(
          METADATA_PATHS.evidencePolicyPreview,
          { section },
          { query: { tier } },
        ),
      ) as PolicyPreview;
    },
    async save(tier, section, note) {
      // `unwrapData` is what turns the resolved `{ok:false}` envelope into a
      // throw. Without it a refused save (no `…:author` scope at that rung, a
      // rejected section) still told the operator "New transactions are judged
      // against it" — about the ladder that decides which evidence is admitted.
      unwrapData(await client.put(path, { section, note }, { query: { tier } }));
      // The ladder read is the thing a save changes the meaning of — it decides
      // what "inherited" says for every rung below. `useConfidencePolicy.save`
      // reloads straight after this, and with nothing dropped that reload was
      // served the PRE-save ladder out of the 30s GET cache: the operator saw
      // their own edit missing and saved again.
      client.invalidateMatching(path);
    },
    async clear(tier) {
      unwrapData(await client.delete(path, { query: { tier } }));
      client.invalidateMatching(path);
    },
  };
}
